<template>
  <AppLayout 
    page-title="Profile & Certifications" 
    page-subtitle="Manage your profile, certifications, and professional information"
  >
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        {{ userProfile.roleLabel || 'Account' }}
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Profile & Certifications</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">Your live account details — not demo data. Change your name, email, or company and save.</p>
    </div>

    <p v-if="errorMessage" class="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300">{{ errorMessage }}</p>
    <p v-if="savedMessage" class="mb-4 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">{{ savedMessage }}</p>

    <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-start space-x-6">
        <div class="flex-shrink-0 relative">
          <div class="w-24 h-24 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center overflow-hidden">
            <img 
              v-if="userProfile.avatarUrl" 
              :src="userProfile.avatarUrl" 
              :alt="userProfile.name"
              class="w-full h-full object-cover"
            />
            <span v-else class="text-2xl font-bold text-white">{{ userProfile.initials }}</span>
          </div>
          <button 
            type="button"
            @click="triggerFileUpload"
            class="absolute -bottom-1 -right-1 w-8 h-8 bg-emerald-500 hover:bg-emerald-600 rounded-full flex items-center justify-center transition-colors"
            title="Upload Photo"
          >
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </button>
          <input 
            ref="fileInput"
            type="file" 
            accept="image/*" 
            class="hidden" 
            @change="handleAvatarUpload"
          />
        </div>
        <div class="flex-1">
          <h2 class="text-2xl font-bold text-white mb-2">{{ userProfile.name || 'Your name' }}</h2>
          <p class="text-emerald-400 font-medium mb-1">{{ userProfile.jobTitle || userProfile.roleLabel }}</p>
          <p class="text-slate-400 mb-4">{{ userProfile.company || userProfile.email }}</p>
          <div class="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-slate-300">
            <span v-if="userProfile.location">{{ userProfile.location }}</span>
            <span v-if="userProfile.experience">{{ userProfile.experience }} years experience</span>
            <span>{{ userProfile.certifications.length }} certifications</span>
          </div>
        </div>
        <div class="flex space-x-3">
          <button
            type="button"
            class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors disabled:opacity-60"
            :disabled="saving || loading"
            @click="editing ? saveProfile() : startEdit()"
          >
            {{ saving ? 'Saving…' : editing ? 'Save Profile' : 'Edit Profile' }}
          </button>
          <button
            v-if="editing"
            type="button"
            class="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors"
            @click="cancelEdit"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <div class="profile-stat-card">
        <p class="text-sm font-medium text-slate-400 mb-1">Field tickets</p>
        <p class="text-3xl font-bold text-white">{{ profileStats.completedJobs }}</p>
      </div>
      <div class="profile-stat-card">
        <p class="text-sm font-medium text-slate-400 mb-1">Active certifications</p>
        <p class="text-3xl font-bold text-white">{{ profileStats.activeCertifications }}</p>
      </div>
      <div class="profile-stat-card">
        <p class="text-sm font-medium text-slate-400 mb-1">Hours this year</p>
        <p class="text-3xl font-bold text-white">{{ profileStats.hoursWorked }}h</p>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div class="lg:col-span-2">
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
          <h3 class="text-xl font-bold text-white mb-4">Personal Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
              <input v-model="userProfile.name" type="text" :readonly="!editing" class="profile-input" placeholder="Your name">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Job Title</label>
              <input v-model="userProfile.jobTitle" type="text" :readonly="!editing" class="profile-input" placeholder="Administrator, Contractor…">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <input v-model="userProfile.email" type="email" :readonly="!editing" class="profile-input" placeholder="you@company.com">
              <p class="text-xs text-slate-500 mt-1">You can switch this to a company email. Use the new address to sign in next time.</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Phone</label>
              <input v-model="userProfile.phone" type="tel" :readonly="!editing" class="profile-input" placeholder="Phone">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Location</label>
              <input v-model="userProfile.location" type="text" :readonly="!editing" class="profile-input" placeholder="City, State">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Your company</label>
              <input v-model="userProfile.company" type="text" :readonly="!editing" class="profile-input" placeholder="Your company name">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Years of experience</label>
              <input v-model="userProfile.experience" type="text" :readonly="!editing" class="profile-input" placeholder="e.g. 8">
            </div>
          </div>
          <div class="mt-6">
            <label class="block text-sm font-medium text-slate-300 mb-2">Bio</label>
            <textarea v-model="userProfile.bio" rows="4" :readonly="!editing" class="profile-input" placeholder="A short professional bio"></textarea>
          </div>
        </div>

        <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xl font-bold text-white">Certifications</h3>
            <button v-if="editing" type="button" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg" @click="addCertification">
              Add Certification
            </button>
          </div>
          <p v-if="!userProfile.certifications.length" class="text-slate-400 text-sm">No certifications yet.{{ editing ? ' Add one while editing.' : ' Click Edit Profile to add them.' }}</p>
          <div class="space-y-4">
            <div v-for="(cert, index) in userProfile.certifications" :key="cert.id || index" class="p-4 bg-slate-700/30 rounded-lg">
              <div v-if="editing" class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input v-model="cert.name" class="profile-input" placeholder="Certification name">
                <input v-model="cert.issuer" class="profile-input" placeholder="Issuer">
                <div class="flex gap-2">
                  <input v-model="cert.expiryDate" type="date" class="profile-input flex-1">
                  <button type="button" class="text-red-400 hover:text-red-300 px-2" @click="removeCertification(index)">Remove</button>
                </div>
              </div>
              <div v-else class="flex items-center justify-between">
                <div>
                  <p class="text-white font-medium">{{ cert.name }}</p>
                  <p class="text-sm text-slate-400">{{ cert.issuer }}<span v-if="cert.expiryDate"> • Expires {{ cert.expiryDate }}</span></p>
                </div>
                <span :class="getCertStatusClass(cert.status)" class="px-3 py-1 rounded-full text-xs font-medium">
                  {{ cert.status }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="space-y-8">
        <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xl font-bold text-white">Skills</h3>
            <button v-if="editing" type="button" class="text-emerald-400 text-sm" @click="addSkill">Add</button>
          </div>
          <p v-if="!userProfile.skills.length" class="text-slate-400 text-sm">No skills listed yet.</p>
          <div class="space-y-3">
            <div v-for="(skill, index) in userProfile.skills" :key="index" class="flex items-center justify-between gap-3">
              <input v-if="editing" v-model="skill.name" class="profile-input flex-1" placeholder="Skill">
              <span v-else class="text-white">{{ skill.name }}</span>
              <div class="flex items-center space-x-2">
                <input v-if="editing" v-model.number="skill.level" type="number" min="0" max="100" class="w-16 profile-input">
                <div v-else class="w-16 bg-slate-700 rounded-full h-2">
                  <div class="bg-emerald-500 h-2 rounded-full" :style="{ width: skill.level + '%' }"></div>
                </div>
                <span class="text-sm text-slate-400 w-10">{{ skill.level }}%</span>
                <button v-if="editing" type="button" class="text-red-400 text-xs" @click="userProfile.skills.splice(index, 1)">x</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import AppLayout from '../components/layout/AppLayout.vue'
import { useUserStore } from '../stores/userStore'

const userStore = useUserStore()
const fileInput = ref<HTMLInputElement>()
const loading = ref(false)
const saving = ref(false)
const editing = ref(false)
const errorMessage = ref('')
const savedMessage = ref('')
let snapshot = ''

const emptyProfile = () => ({
  name: '',
  jobTitle: '',
  roleLabel: '',
  company: '',
  email: '',
  phone: '',
  location: '',
  experience: '',
  bio: '',
  initials: 'U',
  avatarUrl: null as string | null,
  userType: '',
  certifications: [] as Array<{ id?: number; name: string; issuer: string; expiryDate: string; status: string }>,
  skills: [] as Array<{ name: string; level: number }>,
})

const userProfile = reactive(emptyProfile())
const profileStats = reactive({
  completedJobs: 0,
  activeCertifications: 0,
  hoursWorked: 0,
})

function authHeaders(json = true) {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${userStore.authToken || localStorage.getItem('auth_token') || ''}`,
  }
  if (json) headers['Content-Type'] = 'application/json'
  return headers
}

function applyProfile(data: any) {
  Object.assign(userProfile, {
    name: data.name || '',
    jobTitle: data.jobTitle || '',
    roleLabel: data.roleLabel || '',
    company: data.company || '',
    email: data.email || '',
    phone: data.phone || '',
    location: data.location || '',
    experience: data.experience || '',
    bio: data.bio || '',
    initials: data.initials || 'U',
    avatarUrl: data.avatarUrl || null,
    userType: data.userType || '',
    certifications: Array.isArray(data.certifications) ? data.certifications.map((c: any) => ({ ...c })) : [],
    skills: Array.isArray(data.skills) ? data.skills.map((s: any) => ({ ...s })) : [],
  })
  Object.assign(profileStats, data.stats || { completedJobs: 0, activeCertifications: 0, hoursWorked: 0 })
  userStore.updateUser({
    name: userProfile.name,
    email: userProfile.email,
    phone: userProfile.phone,
    location: userProfile.location,
    jobTitle: userProfile.jobTitle,
    company: userProfile.company,
    bio: userProfile.bio,
    avatar: userProfile.avatarUrl || undefined,
  })
}

async function loadProfile() {
  loading.value = true
  errorMessage.value = ''
  try {
    userStore.loadUserFromStorage()
    const res = await fetch('/api/user/profile', { headers: authHeaders() })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Could not load profile')
    applyProfile(data.profile || {})
    snapshot = JSON.stringify(userProfile)
  } catch (e: any) {
    errorMessage.value = e.message || 'Could not load profile'
    const stored = userStore.currentUser
    if (stored) {
      applyProfile({
        name: stored.name,
        email: stored.email,
        phone: stored.phone,
        location: stored.location,
        jobTitle: stored.jobTitle,
        company: stored.company,
        bio: stored.bio,
        avatarUrl: stored.avatar,
        roleLabel: stored.userType === 'admin' ? 'Administrator' : stored.userType,
        initials: userStore.userInitials,
        certifications: [],
        skills: [],
        stats: { completedJobs: 0, activeCertifications: 0, hoursWorked: 0 },
      })
    }
  } finally {
    loading.value = false
  }
}

function startEdit() {
  snapshot = JSON.stringify(userProfile)
  editing.value = true
  savedMessage.value = ''
}

function cancelEdit() {
  if (snapshot) applyProfile(JSON.parse(snapshot))
  editing.value = false
}

async function saveProfile() {
  saving.value = true
  errorMessage.value = ''
  savedMessage.value = ''
  try {
    const res = await fetch('/api/user/profile', {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({
        name: userProfile.name,
        email: userProfile.email,
        phone: userProfile.phone,
        location: userProfile.location,
        jobTitle: userProfile.jobTitle,
        company: userProfile.company,
        bio: userProfile.bio,
        experience: userProfile.experience,
        skills: userProfile.skills,
        certifications: userProfile.certifications,
      }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Could not save profile')
    applyProfile(data.profile || userProfile)
    editing.value = false
    savedMessage.value = data.message || 'Profile saved.'
  } catch (e: any) {
    errorMessage.value = e.message || 'Could not save profile'
  } finally {
    saving.value = false
  }
}

function addCertification() {
  userProfile.certifications.push({ name: '', issuer: '', expiryDate: '', status: 'Active' })
}

function removeCertification(index: number) {
  userProfile.certifications.splice(index, 1)
}

function addSkill() {
  userProfile.skills.push({ name: '', level: 80 })
}

const getCertStatusClass = (status: string) => {
  const classes: Record<string, string> = {
    Active: 'bg-emerald-500/90',
    'Expiring Soon': 'bg-yellow-500/90',
    Expired: 'bg-red-500/90',
  }
  return classes[status] || 'bg-slate-500/90'
}

const triggerFileUpload = () => fileInput.value?.click()

const handleAvatarUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
  if (!allowedTypes.includes(file.type)) {
    errorMessage.value = 'Please select a JPEG, PNG, or GIF image'
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    errorMessage.value = 'File too large. Maximum size is 5MB'
    return
  }
  try {
    const formData = new FormData()
    formData.append('avatar', file)
    const res = await fetch('/api/profile/upload-avatar', {
      method: 'POST',
      headers: { Authorization: authHeaders(false).Authorization },
      body: formData,
    })
    const result = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(result.error || 'Upload failed')
    userProfile.avatarUrl = result.avatarUrl
    userStore.updateUser({ avatar: result.avatarUrl })
    savedMessage.value = 'Profile picture updated.'
  } catch (e: any) {
    errorMessage.value = e.message || 'Error uploading profile picture'
  }
  target.value = ''
}

onMounted(loadProfile)
</script>

<style scoped>
.profile-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300;
}
.profile-input {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 read-only:opacity-80;
}
</style>
